// Client-side device hint for anon-signup farming resistance.
//
// AUTH_PAYMENT_HARDENING_PLAN.md §6.2.a: collect a stable, privacy-respecting
// device hint string. Deliberately low-entropy — we do NOT want to fingerprint
// individuals — but stable enough to detect "same browser, new incognito".
// Shared across normal + incognito windows of the same device.
//
// The raw string never leaves the device in cleartext: it is sent as the
// `x-rc-device-hint` request header on `anon-signup-challenge`
// (issue/verify), which hashes it server-side and stores only the hash in
// `anon_signups.device_hint_hash` (§5.6). Length-checked at 512 bytes by the
// Vercel edge middleware (§6.2.b MIN-AUTH-11 guard) before reaching Deno.
//
// Inputs (exhaustive):
//   - navigator.userAgent       — browser + OS major
//   - navigator.platform        — CPU family
//   - navigator.hardwareConcurrency — logical core count
//   - navigator.language        — primary locale
//   - navigator.deviceMemory    — coarse RAM bucket (non-standard, may be '')
//   - screen.width / height     — physical resolution
//   - screen.colorDepth         — color depth
//   - new Date().getTimezoneOffset() — tz offset in minutes
//
// Deliberately NOT included (privacy-hostile / defeats incognito detection):
//   - canvas fingerprinting, WebGL renderer string, cookies/localStorage,
//     IndexedDB probes, font enumeration, AudioContext fingerprint.

/**
 * Collect a stable, privacy-respecting device hint string.
 *
 * Pure function; no side effects. Returns a `|`-joined string of the inputs
 * listed above. Never throws: missing navigator fields default to the empty
 * string so the join always produces the same slot count.
 */
export async function getDeviceHint(): Promise<string> {
  const nav = typeof navigator !== 'undefined' ? navigator : ({} as Navigator);
  const scr = typeof screen !== 'undefined' ? screen : ({ width: 0, height: 0, colorDepth: 0 } as Screen);
  const parts: Array<string | number> = [
    nav.userAgent ?? '',
    nav.platform ?? '',
    nav.hardwareConcurrency ?? '',
    nav.language ?? '',
    // `deviceMemory` is a non-standard (Chromium-only) Navigator property.
    (nav as Navigator & { deviceMemory?: number }).deviceMemory ?? '',
    scr.width,
    scr.height,
    scr.colorDepth,
    new Date().getTimezoneOffset(),
  ];
  return parts.join('|');
}
